/**
 * Ayarlar kartlarını sabit genişlikli sütunlara yerleştirir (kural ve hesap
 * `lib/sutunYerlesimi.ts`). Kartlar mutlak konumlanır, sırası ve React
 * kimliği değişmez (açık bir form sütun değişince sıfırlanmasın). Yerleri
 * React değil bu bileşen yazar (stil özniteliği). Dar ekranda ve ölçüm
 * yapılamayan ortamda tek sütun, eskisi gibi.
 */

import { Children, useLayoutEffect, useRef, type ReactNode } from "react";
import { ARALIK_PX, KART_PX, konumlar, sutunAta, sutunSayisi } from "../lib/sutunYerlesimi";

export function SutunYerlesimi({ children }: { readonly children: ReactNode }) {
  const kap = useRef<HTMLDivElement>(null);
  const liste = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const k = kap.current;
    const z = liste.current;
    if (k === null || z === null) return undefined;
    let atama: number[] | null = null;
    let atamaAnahtari = "";

    const kartlar = (): HTMLElement[] => (Array.from(z.children) as HTMLElement[]).filter((el) => el.firstElementChild !== null);
    const tekSutun = (): void => {
      atama = null;
      atamaAnahtari = "";
      Object.assign(z.style, { position: "", height: "", width: "", maxWidth: "" });
      for (const el of Array.from(z.children) as HTMLElement[]) Object.assign(el.style, { position: "", left: "", top: "", width: "" });
    };

    const yerlestir = (): void => {
      const sutun = sutunSayisi(k.clientWidth);
      const els = kartlar();
      if (sutun < 2 || els.length < 2) {
        tekSutun();
        return;
      }
      // Boy, kartın kendi (sabit) genişliğinde ölçülür.
      for (const el of els) el.style.width = `${KART_PX}px`;
      const boylar = els.map((el) => el.getBoundingClientRect().height);
      if (boylar.some((h) => !(h > 0))) {
        tekSutun();
        return;
      }
      const anahtar = `${sutun}:${els.length}`;
      if (atama === null || anahtar !== atamaAnahtari) {
        atama = sutunAta(boylar, sutun);
        atamaAnahtari = anahtar;
      }
      const kullanilan = Math.min(sutun, els.length);
      const { yerler, yukseklik } = konumlar(atama, boylar);
      Object.assign(z.style, {
        position: "relative",
        maxWidth: "none",
        height: `${yukseklik}px`,
        width: `${kullanilan * KART_PX + (kullanilan - 1) * ARALIK_PX}px`,
      });
      els.forEach((el, i) => {
        const y = yerler[i]!;
        Object.assign(el.style, { position: "absolute", left: `${y.sol}px`, top: `${y.ust}px`, width: `${KART_PX}px` });
      });
    };

    let bekleyen = 0;
    const planla = (): void => {
      cancelAnimationFrame(bekleyen);
      bekleyen = requestAnimationFrame(yerlestir);
    };
    yerlestir();

    // Kap genişliği ve kartların boyu (kart açılıp uzayınca) izlenir.
    const boyut = new ResizeObserver(planla);
    boyut.observe(k);
    for (const el of Array.from(z.children)) boyut.observe(el);
    const icerik = new MutationObserver(() => {
      for (const el of Array.from(z.children)) boyut.observe(el);
      planla();
    });
    icerik.observe(z, { childList: true });
    return () => {
      cancelAnimationFrame(bekleyen);
      boyut.disconnect();
      icerik.disconnect();
    };
  }, []);

  return (
    <div ref={kap} className="w-full">
      <div ref={liste} data-sutun-yerlesimi="" className="mx-auto grid max-w-3xl gap-4">
        {Children.map(children, (kart) => (
          <div className="min-w-0 empty:hidden">{kart}</div>
        ))}
      </div>
    </div>
  );
}
